import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'agency',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await register(formData.email, formData.password, formData.name, formData.role);
      toast.success('Account created successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1759178459554-c27da3a5ecc6?crop=entropy&cs=srgb&fm=jpg&q=85')`,
        }}
      >
        <div className="absolute inset-0 bg-zinc-900/30" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="text-4xl text-white font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Elevate Your Content Game
          </h2>
          <p className="text-white/80 text-lg">
            Join the platform that transforms ideas into stunning reels
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#FAFAFA]">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <h1 
              className="text-4xl font-medium text-zinc-900 mb-2"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Create Account
            </h1>
            <p className="text-zinc-500">Join the Content OS for your team</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">
                Full Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full input-flush mt-2"
                placeholder="Enter your name"
                required
                data-testid="register-name-input"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full input-flush mt-2"
                placeholder="Enter your email"
                required
                data-testid="register-email-input"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full input-flush mt-2 pr-10"
                  placeholder="Create a password"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Role is fixed to agency for new registrations */}
            <input type="hidden" value="agency" />

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-zinc-900 text-white hover:bg-zinc-800 rounded-sm py-6 text-sm uppercase tracking-widest font-medium transition-all"
              data-testid="register-submit-button"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <p className="mt-8 text-center text-zinc-500">
            Already have an account?{' '}
            <Link to="/login" className="text-zinc-900 hover:text-[#D4AF37] font-medium transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
